import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '../../lib/api';
import type { TimeEntry, Employee } from '../../types';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Input, Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Textarea,
} from '@werkstatt/ui';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../components/ui/form';

const schema = z.object({
  employee_id: z.string().min(1, 'Pflichtfeld'),
  date: z.string().min(1, 'Pflichtfeld'),
  clock_in: z.string().min(1, 'Pflichtfeld'),
  clock_out: z.string().optional(),
  break_minutes: z.coerce.number().min(0).default(0),
  entry_type: z.string().default('work'),
  note: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onClose: () => void;
  entry?: TimeEntry | null;
}

export function TimeEntryDialog({ open, onClose, entry }: Props) {
  const queryClient = useQueryClient();

  const { data: employees = [] } = useQuery({
    queryKey: ['employees', true],
    queryFn: async () => {
      const res = await api.get<Employee[]>('/employees/?is_active=true');
      return res.data;
    },
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      employee_id: '',
      date: new Date().toISOString().split('T')[0],
      clock_in: '',
      clock_out: '',
      break_minutes: 0,
      entry_type: 'work',
      note: '',
    },
  });

  useEffect(() => {
    if (entry) {
      form.reset({
        employee_id: entry.employee_id,
        date: entry.date,
        clock_in: entry.clock_in ? entry.clock_in.slice(0, 16) : '',
        clock_out: entry.clock_out ? entry.clock_out.slice(0, 16) : '',
        break_minutes: entry.break_minutes,
        entry_type: entry.entry_type,
        note: entry.note || '',
      });
    } else {
      form.reset({
        employee_id: '',
        date: new Date().toISOString().split('T')[0],
        clock_in: '',
        clock_out: '',
        break_minutes: 0,
        entry_type: 'work',
        note: '',
      });
    }
  }, [entry, open, form]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload = {
        ...values,
        clock_out: values.clock_out || undefined,
        note: values.note || undefined,
      };
      if (entry) {
        return api.put(`/time_entries/${entry.id}`, payload);
      }
      return api.post('/time_entries/', payload);
    },
    onSuccess: () => {
      toast.success(entry ? 'Zeitbuchung aktualisiert' : 'Zeitbuchung erstellt');
      queryClient.invalidateQueries({ queryKey: ['time-entries'] });
      onClose();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Fehler beim Speichern';
      toast.error(msg);
    },
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">{entry ? 'Zeitbuchung bearbeiten' : 'Zeitbuchung erstellen'}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
            <FormField control={form.control} name="employee_id" render={({ field }) => (
              <FormItem>
                <FormLabel>Mitarbeiter *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Mitarbeiter wählen" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {employees.map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.last_name}, {e.first_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />

            <FormField control={form.control} name="date" render={({ field }) => (
              <FormItem>
                <FormLabel>Datum *</FormLabel>
                <FormControl><Input type="date" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="clock_in" render={({ field }) => (
                <FormItem>
                  <FormLabel>Einbuchen *</FormLabel>
                  <FormControl><Input type="datetime-local" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="clock_out" render={({ field }) => (
                <FormItem>
                  <FormLabel>Ausbuchen</FormLabel>
                  <FormControl><Input type="datetime-local" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="break_minutes" render={({ field }) => (
                <FormItem>
                  <FormLabel>Pause (Minuten)</FormLabel>
                  <FormControl><Input type="number" min="0" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="entry_type" render={({ field }) => (
                <FormItem>
                  <FormLabel>Typ</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="work">Arbeit</SelectItem>
                      <SelectItem value="overtime">Überstunden</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )} />
            </div>

            <FormField control={form.control} name="note" render={({ field }) => (
              <FormItem>
                <FormLabel>Notiz</FormLabel>
                <FormControl><Textarea rows={2} {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>Abbrechen</Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Speichern...' : 'Speichern'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
